import { useEffect, useRef, useState } from "react";
import { API, axiosInstance, useAuth } from "../App";
import { Button } from "./ui/button";

// Public release configuration, never location/query/form supplied redirects.
const APPROVED_APIS = new Set([
  "https://cinch-sports-hq-staging-staging.up.railway.app/api",
  "https://api.sporthouse.ai/api",
]);

function Registration({ user, onRegistered }) {
  const [organizationName, setOrganizationName] = useState(user.org_name || "");
  const [organization, setOrganization] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [clientId, setClientId] = useState("");
  const [error, setError] = useState("");
  const busy = useRef(false);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => {
    let cancelled = false;
    if (APPROVED_APIS.has(API)) axiosInstance.get("/organizations/my-organizations").then(({ data }) => {
      const current = Array.isArray(data) ? data.find(org => org?.org_id === user.org_id) : null;
      if (!cancelled && typeof current?.name === "string" && current.name.trim()) setOrganizationName(current.name);
    }).catch(() => { /* Display enrichment never changes registration authority. */ });
    return () => { cancelled = true; };
  }, [user.org_id]);
  if (!APPROVED_APIS.has(API)) return <p>MCP registration is unavailable for this API configuration.</p>;

  const register = async () => {
    if (busy.current || attempted || organization !== user.org_id) return;
    busy.current = true;
    setAttempted(true);
    try {
      // Selection confirms current session context; the API never accepts a caller org override.
      const { data: current } = await axiosInstance.get("/auth/me");
      if (!active.current) return;
      if (current.user_id !== user.user_id || current.org_id !== organization || current.role !== "org_admin") {
        setError("Session or organization changed. Reload and confirm the intended organization.");
        return;
      }
      const response = await axiosInstance.post("/developer/apps", {
        name: "Sporthouse MCP",
        description: "Read-only Sporthouse MCP program access.",
        authority_policy: "mcp_session_v1",
        expected_org_id: organization,
        scopes: ["programs:read"],
        redirect_uri: `${API}/mcp/callback`,
        webhook_url: null,
        webhook_events: [],
      });
      // PKCE needs no secret. Never retain, display, copy or log the response object.
      const id = response.data?.app?.client_id;
      if (active.current) {
        if (typeof id === "string" && /^client_[a-zA-Z0-9_-]+$/.test(id)) {
          setClientId(id);
          onRegistered?.();
        }
        else setError("Registration could not be confirmed. Check existing applications before trying again.");
      }
    } catch (failure) {
      if (active.current) setError(failure.response?.status === 403
        ? "Organization administrator access required. No access has been granted."
        : "Registration could not be confirmed. Check existing applications before trying again.");
    }
  };

  return <section className="rounded-[28px] border border-[#dfe6df] bg-white p-6 space-y-4">
    <h2 className="text-2xl font-semibold">Register Sporthouse MCP</h2>
    <p>Read-only programs:read access using PKCE. Registration does not authorize access; separate consent is required.</p>
    <p>Confirm your current organization. To use another organization, switch organization in your account first, then return here. Check Your applications before creating a duplicate.</p>
    <label className="block">MCP organization
      <select className="block rounded border p-2" value={organization} disabled={attempted} onChange={event => setOrganization(event.target.value)}>
        <option value="">Select intended organization</option>
        <option value={user.org_id}>{organizationName || user.org_id}</option>
      </select>
    </label>
    <p>Fixed callback: <code>{API}/mcp/callback</code></p>
    <Button type="button" disabled={attempted || organization !== user.org_id} onClick={register}>Register MCP client</Button>
    {error ? <p role="alert">{error}</p> : null}
    {clientId ? <div role="status">MCP client registered. Public client ID: <code>{clientId}</code>. No access granted. No secret is needed for PKCE.</div> : null}
  </section>;
}

export default function McpRegistration({ onRegistered }) {
  const { user } = useAuth();
  if (!user?.user_id || !user?.org_id || user.role !== "org_admin") return null;
  return <Registration key={`${user.user_id}:${user.org_id}:${API}`} user={user} onRegistered={onRegistered} />;
}
